import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, { type TableColumn } from "react-data-table-component";
import { TablePager } from "../../components/shared/TablePager";
import { adminTableTheme } from "../../components/admin/adminTableTheme";
import { AdminGate } from "../../components/admin/AdminGate";
import { EmptyState, ErrorState, LoadingState } from "../../components/states";
import { useToast } from "../../components/toast/toastContext";
import styles from "../../components/admin/Admin.module.css";
import pageStyles from "./ModelNamesPage.module.css";
import { PageIntro } from "../../components/admin/PageIntro";
import { formatMoney } from "../../components/consumption/formatters";
import { fetchModelNames, saveModelNames, type ModelNameEntry } from "../../api/modelNames";

const SettingsWritePermission = "settings.write";

/** The key a row's draft is kept under: vendor and raw name together, since two vendors can report the same name. */
function rowKey(entry: Pick<ModelNameEntry, "vendorId" | "rawName">): string {
  return `${entry.vendorId}\u0000${entry.rawName}`;
}

/** A copy of a draft map without one row's draft, so that row shows its saved name again. */
function withoutKey(drafts: Record<string, string>, key: string): Record<string, string> {
  const next = { ...drafts };
  delete next[key];
  return next;
}

/**
 * Whether a row matches the search: the text, ignoring case, is contained in its vendor, its name as reported or its display name (the
 * one being typed, if any).
 */
function matchesSearch(row: ModelNameRow, draft: string | undefined, search: string): boolean {
  const term = search.trim().toLocaleLowerCase();
  if (term === "") {
    return true;
  }
  return [row.vendorId, row.rawName, draft ?? row.displayName ?? ""].some((text) => text.toLocaleLowerCase().includes(term));
}

/** A model row as the table shows it, with the key the table needs to tell two rows apart. */
interface ModelNameRow extends ModelNameEntry {
  key: string;
}

/**
 * The "Model names" screen (`AIA-81`): every model name the portal has seen, with its all-time gross
 * spend at list price and the display name it is grouped and labelled by. Two raw names that share a
 * display name are reported as one model everywhere the portal groups by model. Gated on `settings.write`,
 * the same permission as the currency rates it sits beside.
 */
export function ModelNamesPage(): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div>
      <AdminGate permissions={[SettingsWritePermission]} title={t("admin.modelNames.denied.title")} description={t("admin.modelNames.denied.description")}>
        <ModelNamesContent />
      </AdminGate>
    </div>
  );
}

export function ModelNamesContent(): React.JSX.Element {
  const { t } = useTranslation();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded"; entries: ModelNameEntry[] } | { kind: "error"; message: string }>({ kind: "loading" });
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // Rows saved during this visit stay listed under "Show unmapped only", so a name typed there does not vanish on blur.
  const [savedKeys, setSavedKeys] = useState<Set<string>>(() => new Set());
  const [unmappedOnly, setUnmappedOnly] = useState(false);
  const [search, setSearch] = useState("");
  const toast = useToast();

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchModelNames();
    if (result.kind === "ok") {
      setState({ kind: "loaded", entries: result.response });
    } else if (result.kind === "denied") {
      setState({ kind: "error", message: t("admin.modelNames.denied.description") });
    } else {
      setState({ kind: "error", message: result.message });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const entries = useMemo<ModelNameEntry[]>(() => (state.kind === "loaded" ? state.entries : []), [state]);

  /** Saves one row's edited name when its text box loses focus. An unchanged or reverted name is not sent. */
  const saveRow = useCallback(
    async (entry: ModelNameEntry) => {
      const key = rowKey(entry);
      const draft = drafts[key];
      if (draft === undefined) {
        return;
      }

      const name = draft.trim();
      if (name === (entry.displayName ?? "")) {
        setDrafts((current) => withoutKey(current, key));
        return;
      }

      const result = await saveModelNames([{ vendorId: entry.vendorId, rawName: entry.rawName, displayName: name === "" ? null : name }]);
      if (result.kind === "ok") {
        setState({ kind: "loaded", entries: result.response });
        setDrafts((current) => withoutKey(current, key));
        setSavedKeys((current) => new Set(current).add(key));
        toast.show(t("admin.modelNames.saved", { name: entry.rawName }), "success");
      } else {
        toast.show(result.kind === "denied" ? t("admin.denied.description", { permissions: SettingsWritePermission }) : result.message, "error");
      }
    },
    [drafts, t, toast],
  );

  const columns = useMemo<TableColumn<ModelNameRow>[]>(
    () => [
      { id: "vendor", name: t("admin.modelNames.table.vendor"), selector: (row) => row.vendorId, sortable: true },
      { id: "rawName", name: t("admin.modelNames.table.rawName"), selector: (row) => row.rawName, sortable: true },
      {
        id: "grossSpend",
        name: t("admin.modelNames.table.grossSpend"),
        selector: (row) => row.grossSpendUsd,
        sortable: true,
        right: true,
        cell: (row) => formatMoney(row.grossSpendUsd, "USD"),
      },
      {
        id: "displayName",
        name: t("admin.modelNames.table.displayName"),
        grow: 2,
        // Sorted by the saved name, not what is being typed, so a row does not jump while it is edited; unnamed models sort together first.
        selector: (row) => row.displayName ?? "",
        sortable: true,
        sortFunction: (a, b) => (a.displayName ?? "").localeCompare(b.displayName ?? "", undefined, { sensitivity: "base", numeric: true }),
        cell: (row) => (
          <input
            className={styles.modelNameInput}
            aria-label={t("admin.modelNames.table.displayNameFor", { name: row.rawName })}
            value={drafts[rowKey(row)] ?? row.displayName ?? ""}
            placeholder={row.rawName}
            onChange={(event) => setDrafts((current) => ({ ...current, [rowKey(row)]: event.target.value }))}
            onBlur={() => void saveRow(row)}
          />
        ),
      },
    ],
    [t, drafts, saveRow],
  );

  if (state.kind === "loading") {
    return <LoadingState label={t("admin.modelNames.loading")} />;
  }

  if (state.kind === "error") {
    return <ErrorState title={t("admin.modelNames.errorTitle")} description={state.message} onRetry={() => void load()} />;
  }

  const rows: ModelNameRow[] = entries.map((entry) => ({ ...entry, key: rowKey(entry) }));
  const unmapped = unmappedOnly ? rows.filter((row) => !row.displayName || drafts[row.key] !== undefined || savedKeys.has(row.key)) : rows;
  const visible = unmapped.filter((row) => matchesSearch(row, drafts[row.key], search));

  return (
    <>
      <PageIntro>{t("admin.modelNames.intro")}</PageIntro>
      <div className={`${styles.form} ${pageStyles.toolbar}`}>
        <input
          type="search"
          className={pageStyles.search}
          aria-label={t("admin.modelNames.search")}
          placeholder={t("admin.modelNames.searchPlaceholder")}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <label className={styles.toolbarToggle}>
          <input type="checkbox" className={styles.allocationToggle} checked={unmappedOnly} onChange={(event) => setUnmappedOnly(event.target.checked)} /> {t("admin.modelNames.unmappedOnly")}
        </label>
      </div>
      {entries.length === 0 ? (
        <EmptyState title={t("admin.modelNames.emptyTitle")} description={t("admin.modelNames.emptyDescription")} />
      ) : (
        <div className={`${styles.tableWrapper} ${styles.peopleTableWrapper}`}>
          <DataTable
            columns={columns}
            data={visible}
            className={styles.peopleDataTable}
            keyField="key"
            ariaLabel={t("admin.modelNames.title")}
            theme={adminTableTheme}
            defaultSortFieldId="grossSpend"
            defaultSortAsc={false}
            noDataComponent={<p className={styles.note}>{t("admin.modelNames.noMatches")}</p>}
            pagination
            paginationComponent={TablePager}
            paginationPerPage={25}
            paginationRowsPerPageOptions={[25, 50, 100]}
            striped
            highlightOnHover
            columnSeparator="subtle"
          />
        </div>
      )}
    </>
  );
}
